import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { subDays } from "date-fns/subDays";
import { format } from "date-fns/format";
import { getDateFnsLocale } from "@tellescope/utils";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { ToggleGroup, ToggleGroupItem } from "@tellescope/ui";
import type { SeriesPoint } from "./metrics";

type ChartType = "line" | "bar";
type RangeKey = "7" | "30" | "90" | "all";

const RANGE_DAYS: Record<Exclude<RangeKey, "all">, number> = { "7": 7, "30": 30, "90": 90 };
const RANGE_KEYS: RangeKey[] = ["7", "30", "90", "all"];

const isChartType = (value: string): value is ChartType => value === "line" || value === "bar";
const isRangeKey = (value: string): value is RangeKey => RANGE_KEYS.some((key) => key === value);

const cutoffFor = (range: RangeKey): Date | null => (range === "all" ? null : subDays(new Date(), RANGE_DAYS[range]));

interface FilteredPoint extends SeriesPoint {
  dateLabel: string;
  isSelected: boolean;
}

/**
 * Recharts' own click-handler type (`CategoricalChartFunc`/`MouseHandlerDataParam`) omits
 * `activePayload` even though it's the field Recharts actually passes on click — a known gap in
 * its v3 type definitions (present in v2's types, dropped in the v3 rewrite; runtime unchanged).
 * Narrowed via a runtime guard (below) rather than asserted, since the declared type has no
 * structural overlap with what's actually passed.
 */
interface ChartClickState {
  activePayload?: { payload?: FilteredPoint }[];
}

const isChartClickState = (value: unknown): value is ChartClickState => typeof value === "object" && value !== null;

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: FilteredPoint;
}

const CustomDot = ({ cx = 0, cy = 0, payload }: CustomDotProps) => (
  <circle
    cx={cx}
    cy={cy}
    r={payload?.isSelected ? 6 : 3.5}
    fill="var(--color-primary, #171717)"
    stroke="white"
    strokeWidth={2}
  />
);

interface CustomBarProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: FilteredPoint;
}

const CustomBar = ({ x = 0, y = 0, width = 0, height = 0, payload }: CustomBarProps) => (
  <rect
    x={x}
    y={y}
    width={width}
    height={Math.max(0, height)}
    rx={4}
    ry={4}
    fill={payload?.isSelected ? "var(--color-primary, #171717)" : "#e5e7eb"}
  />
);

interface MetricTrendChartProps {
  series: SeriesPoint[];
  unit: string;
  name: string;
  target?: number;
  selectedDate?: string;
  onSelectPoint?: (point: SeriesPoint) => void;
}

export const MetricTrendChart = ({
  series,
  unit,
  name,
  target,
  selectedDate,
  onSelectPoint,
}: MetricTrendChartProps) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);
  const [chartType, setChartType] = useState<ChartType>("line");
  const [range, setRange] = useState<RangeKey>("30");

  const handleChartTypeChange = (value: string) => {
    if (isChartType(value)) setChartType(value);
  };
  const handleRangeChange = (value: string) => {
    if (isRangeKey(value)) setRange(value);
  };

  const filtered = useMemo<FilteredPoint[]>(() => {
    const cutoff = cutoffFor(range);
    return series
      .filter((p) => (cutoff ? new Date(p.date) >= cutoff : true))
      .map((p) => ({
        ...p,
        dateLabel: format(new Date(p.date), "MMM d", { locale }),
        isSelected: p.date === selectedDate,
      }));
  }, [series, range, selectedDate, locale]);

  const { yMin, yMax } = useMemo(() => {
    const values = filtered.map((d) => d.value);
    const minVal = values.length ? Math.min(...values) : 0;
    const maxVal = values.length ? Math.max(...values) : 1;
    const pad = (maxVal - minVal) * 0.3 || 1;
    return {
      yMin: Math.max(0, Math.floor(minVal - pad)),
      yMax: Math.ceil(Math.max(maxVal, target ?? 0) + pad),
    };
  }, [filtered, target]);

  const handleClick = (state: unknown) => {
    const point = isChartClickState(state) ? state.activePayload?.[0]?.payload : undefined;
    if (point) onSelectPoint?.(point);
  };

  const tooltipContentStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 12,
    padding: "6px 10px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
  };
  const tooltipLabelStyle = { color: "#6b7280", marginBottom: 2 };
  const axisTick = { fontSize: 11, fill: "#9ca3af" };
  const formatYAxisTick = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <ToggleGroup type="single" value={chartType} onValueChange={handleChartTypeChange}>
          <ToggleGroupItem value="line">{t("Line")}</ToggleGroupItem>
          <ToggleGroupItem value="bar">{t("Bar")}</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup type="single" value={range} onValueChange={handleRangeChange}>
          <ToggleGroupItem value="7">{t("7D")}</ToggleGroupItem>
          <ToggleGroupItem value="30">{t("30D")}</ToggleGroupItem>
          <ToggleGroupItem value="90">{t("90D")}</ToggleGroupItem>
          <ToggleGroupItem value="all">{t("All")}</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {filtered.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
          {t("No data for this period")}
        </div>
      ) : (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "line" ? (
              <LineChart data={filtered} margin={{ top: 8, right: 16, left: -8, bottom: 0 }} onClick={handleClick}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="dateLabel" tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis
                  domain={[yMin, yMax]}
                  tick={axisTick}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  tickFormatter={formatYAxisTick}
                />
                <Tooltip
                  contentStyle={tooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(value: number) => [`${value} ${unit}`, name]}
                />
                {target !== undefined && (
                  <ReferenceLine y={target} stroke="#9ca3af" strokeDasharray="4 4" strokeOpacity={0.7} />
                )}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--color-accent, #171717)"
                  strokeWidth={2}
                  dot={({ cx, cy, payload }: CustomDotProps) => (
                    <CustomDot key={payload?.date} cx={cx} cy={cy} payload={payload} />
                  )}
                  activeDot={{ r: 6, fill: "var(--color-primary, #171717)", stroke: "white", strokeWidth: 2 }}
                />
              </LineChart>
            ) : (
              <BarChart
                data={filtered}
                margin={{ top: 8, right: 16, left: -8, bottom: 0 }}
                barSize={20}
                onClick={handleClick}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="dateLabel" tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis
                  domain={[yMin, yMax]}
                  tick={axisTick}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  tickFormatter={formatYAxisTick}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  contentStyle={tooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(value: number) => [`${value} ${unit}`, name]}
                />
                {target !== undefined && (
                  <ReferenceLine y={target} stroke="#9ca3af" strokeDasharray="4 4" strokeOpacity={0.7} />
                )}
                <Bar dataKey="value" shape={<CustomBar />}>
                  {filtered.map((entry) => (
                    <Cell key={entry.date} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {target !== undefined && (
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-px border-t-2 border-dashed border-gray-400 opacity-70" />
          <span className="text-[11px] text-muted-foreground">
            {t("Daily target {{target}} {{unit}}", { target: target.toLocaleString(), unit })}
          </span>
        </div>
      )}
    </div>
  );
};
